interface ValueRange {
  min: number;
  max: number;
  isOrCondition?: boolean;
  subRanges?: Array<{
    min: number;
    max: number;
    minInclusive?: boolean;
    maxInclusive?: boolean;
  }>;
  minInclusive?: boolean; // 是否包含最小值
  maxInclusive?: boolean; // 是否包含最大值
}

export function areConditionsMutuallyExclusive(
  conditions: Condition[]
): boolean {
  if (conditions.length < 2) return false;

  // 获取所有条件的值域范围
  const ranges = conditions
    .map((condition) => getConditionRange(condition))
    .filter((range): range is ValueRange => range !== null);

  // 如果任一条件没有有效范围，则不互斥
  if (ranges.length !== conditions.length) return false;

  // 检查是否存在任意两个范围有交集
  for (let i = 0; i < ranges.length; i++) {
    for (let j = i + 1; j < ranges.length; j++) {
      if (hasOverlap(ranges[i], ranges[j])) {
        return false;
      }
    }
  }

  return true;
}

function getConditionRange(condition: Condition): ValueRange | null {
  if (!condition.children.length) return null;

  const compareRanges = condition.children
    .filter((compare) => compare.type === 'when')
    .map((compare) => getCompareRange(compare))
    .filter((range): range is ValueRange => range !== null);

  if (!compareRanges.length) return null;

  // 如果是 or 关系，保存所有子区间
  if (condition.logicalOperator === 'or') {
    return {
      min: Math.min(...compareRanges.map((r) => r.min)),
      max: Math.max(...compareRanges.map((r) => r.max)),
      isOrCondition: true,
      subRanges: compareRanges.map((r) => ({
        min: r.min,
        max: r.max,
        minInclusive: r.minInclusive,
        maxInclusive: r.maxInclusive
      }))
    };
  }

  // 如果是 and 关系，取交集
  return compareRanges.reduce((result, range) => ({
    min: Math.max(result.min, range.min),
    max: Math.min(result.max, range.max),
    isOrCondition: false,
    minInclusive:
      result.min === range.min ? range.minInclusive : result.minInclusive,
    maxInclusive:
      result.max === range.max ? range.maxInclusive : result.maxInclusive
  }));
}

function getCompareRange(compare: Compare): ValueRange | null {
  if (!compare.children?.length) return null;

  const paramRanges = compare.children
    .map((param) => getParamRange(param))
    .filter((range): range is ValueRange => range !== null);

  if (!paramRanges.length) return null;

  // 如果是 or 关系，保持独立的范围
  if (compare.logicalOperator === 'or') {
    return {
      min: Math.min(...paramRanges.map((r) => r.min)),
      max: Math.max(...paramRanges.map((r) => r.max)),
      isOrCondition: true,
      subRanges: paramRanges.map((r) => ({
        min: r.min,
        max: r.max,
        minInclusive: r.minInclusive,
        maxInclusive: r.maxInclusive
      }))
    };
  }

  // 如果是 and 关系或未指定，取交集
  return paramRanges.reduce((result, range) => {
    const newMin = Math.max(result.min, range.min);
    const newMax = Math.min(result.max, range.max);
    return {
      min: newMin,
      max: newMax,
      isOrCondition: false,
      minInclusive:
        newMin === result.min
          ? result.minInclusive
          : newMin === range.min
          ? range.minInclusive
          : true,
      maxInclusive:
        newMax === result.max
          ? result.maxInclusive
          : newMax === range.max
          ? range.maxInclusive
          : true
    };
  });
}

function getParamRange(param: CompareParam): ValueRange | null {
  if (
    param.flag !== 'compare-param' ||
    !param.whenOperator ||
    param.whenValue === undefined
  ) {
    return null;
  }

  // 将值转换为两位小数
  const value = Number(param.whenValue.toFixed(2));

  switch (param.whenOperator) {
    case 'LessThan':
      return {
        min: -Infinity,
        max: value,
        minInclusive: false,
        maxInclusive: false
      };
    case 'LessThanOrEqual':
      return {
        min: -Infinity,
        max: value,
        minInclusive: false,
        maxInclusive: true
      };
    case 'Equal':
      return { min: value, max: value, minInclusive: true, maxInclusive: true };
    case 'MoreThan':
      return {
        min: value,
        max: Infinity,
        minInclusive: false,
        maxInclusive: false
      };
    case 'MoreThanOrEqual':
      return {
        min: value,
        max: Infinity,
        minInclusive: true,
        maxInclusive: false
      };
    default:
      return null;
  }
}

function hasOverlap(range1: ValueRange, range2: ValueRange): boolean {
  // 数据源最多为两位小数
  const precision = 0.01;

  // 将值转换为两位小数
  const r1min = Number(
    range1.min === -Infinity ? range1.min : range1.min.toFixed(2)
  );
  const r1max = Number(
    range1.max === Infinity ? range1.max : range1.max.toFixed(2)
  );
  const r2min = Number(
    range2.min === -Infinity ? range2.min : range2.min.toFixed(2)
  );
  const r2max = Number(
    range2.max === Infinity ? range2.max : range2.max.toFixed(2)
  );

  // 检查两个区间是否有重叠
  function checkRangeOverlap(
    range1: {
      min: number;
      max: number;
      minInclusive?: boolean;
      maxInclusive?: boolean;
    },
    range2: {
      min: number;
      max: number;
      minInclusive?: boolean;
      maxInclusive?: boolean;
    }
  ): boolean {
    // 检查边界值是否相等
    const minEqual = Math.abs(range1.min - range2.min) < precision;
    const maxEqual = Math.abs(range1.max - range2.max) < precision;
    const crossMinEqual = Math.abs(range1.max - range2.min) < precision;
    const crossMaxEqual = Math.abs(range1.min - range2.max) < precision;

    // 如果两个区间的边界完全相同
    if (minEqual && maxEqual) {
      return !!(
        (range1.minInclusive && range2.minInclusive) ||
        (range1.maxInclusive && range2.maxInclusive)
      );
    }

    // 如果一个区间的最大值等于另一个区间的最小值
    if (crossMinEqual) {
      return !!(range1.maxInclusive && range2.minInclusive);
    }
    if (crossMaxEqual) {
      return !!(range2.maxInclusive && range1.minInclusive);
    }

    // 检查区间是否有内部重叠
    if (range1.max > range2.min && range1.min < range2.max) {
      return true;
    }

    return false;
  }

  // 对于 or 条件的情况，需要检查是否存在任意一个区间有重叠
  if (range1.isOrCondition || range2.isOrCondition) {
    const ranges1 = range1.isOrCondition
      ? range1.subRanges
      : [
          {
            min: r1min,
            max: r1max,
            minInclusive: range1.minInclusive,
            maxInclusive: range1.maxInclusive
          }
        ];
    const ranges2 = range2.isOrCondition
      ? range2.subRanges
      : [
          {
            min: r2min,
            max: r2max,
            minInclusive: range2.minInclusive,
            maxInclusive: range2.maxInclusive
          }
        ];

    for (const r1 of ranges1 ?? []) {
      for (const r2 of ranges2 ?? []) {
        if (checkRangeOverlap(r1, r2)) {
          return true; // 只要有一个区间重叠就返回 true
        }
      }
    }
    return false; // 所有区间都不重叠
  }

  // 对于普通条件，使用相同的检查逻辑
  return checkRangeOverlap(
    {
      min: r1min,
      max: r1max,
      minInclusive: range1.minInclusive,
      maxInclusive: range1.maxInclusive
    },
    {
      min: r2min,
      max: r2max,
      minInclusive: range2.minInclusive,
      maxInclusive: range2.maxInclusive
    }
  );
}
